import React, { useEffect, useState } from 'react';
import { Row, Col, Table, Input, message, Typography, Spin, Tag, Select, Button, Space, Switch, Popconfirm } from 'antd';
import {
    getAvancesIndicadoresProyecto,
    updateAvanceIndicadorProyecto,
    deleteIndicator
} from '../../../services/indicadoresPO/indicadoresService';
import IndicadoresLineChart from './IndicadoresLineChart';
import { EditOutlined, SaveOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { selectAppPermissions } from '../../../store/auth/selectors';

const { Title } = Typography;
const { Option } = Select;

const AvancesIndicadores = ({ oidProyecto }) => {
    const [avances, setAvances] = useState([]);
    const [filteredAvances, setFilteredAvances] = useState([]);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [tipoFiltro, setTipoFiltro] = useState('');
    const [isTableView, setIsTableView] = useState(true);
    const [editingKey, setEditingKey] = useState(null);
    const [editingValues, setEditingValues] = useState({});

    const [recordToDelete, setRecordToDelete] = useState(null);

    const appName = 'avancesIndicadoresPO';
    const permissions = useSelector((state) => selectAppPermissions(state, appName));
    const permissionUpdate = permissions.priv_update === 'Y';
    const permissionDelete = permissions.priv_delete === 'Y';


    // Meses a editar
    const meses = ['dato_inicial', 'm1', 'm2', 'm3', 'm4', 'm5', 'm6', 'm7', 'm8', 'm9', 'm10'];
    const etiquetasMeses = ['Valor inicial', 'Mes 1', 'Mes 2', 'Mes 3', 'Mes 4', 'Mes 5', 'Mes 6', 'Mes 7', 'Mes 8', 'Mes 9', 'Mes 10'];
    const tipoIndicadorColors = {
        'Agricola productivo': 'green',
        'Pecuario productivo': 'gold',
        'Género y diferencial': 'magenta',
        'Servicios': 'blue',
        'Socio-empresarial-Comercio': 'volcano'
    };

    // Carga inicial
    useEffect(() => {
        const fetchAvances = async () => {
            setLoading(true);
            try {
                const { status, data } = await getAvancesIndicadoresProyecto(oidProyecto);
                if (status === 200) {
                    setAvances(data);
                    setFilteredAvances(data);
                    message.success('Avances cargados correctamente.');
                }
            } catch {
                message.error('Error al cargar avances.');
            } finally {
                setLoading(false);
            }
        };
        if (oidProyecto) fetchAvances();
    }, [oidProyecto]);

    // Filtrado
    useEffect(() => {
        let result = [...avances];
        const sv = searchText.toLowerCase();
        if (sv) result = result.filter(i => i.indicador_nombre.toLowerCase().includes(sv));
        if (tipoFiltro) result = result.filter(i => i.tipo_indicador.trim().toLowerCase() === tipoFiltro.trim().toLowerCase());
        setFilteredAvances(result);
    }, [avances, searchText, tipoFiltro]);

    const startEdit = record => {
        setEditingKey(record.oid);
        setEditingValues({ [record.oid]: { ...record } });
    };

    const handleConfirm = async () => {
        const oid = recordToDelete?.oid;                // <-- aquí
        if (!oid) {
            message.error('No hay indicador seleccionado');
            setRecordToDelete(null);
            return;
        }

        try {
            const result = await deleteIndicator(oid);    // usar oid en lugar de id

            switch (result.status) {
                case 200:
                    // Filtramos por oid
                    setAvances(prev => prev.filter(item => item.oid !== oid));
                    setFilteredAvances(prev => prev.filter(item => item.oid !== oid));
                    message.success(result.message || 'Indicador eliminado correctamente');
                    break;
                // ... resto de casos igual
            }
        } catch (err) {
            message.error(err.message || 'Error al comunicarse con el servidor');
        } finally {
            setRecordToDelete(null);
        }
    };




    const handleCancel = () => {
        message.info('Eliminación cancelada');
        setRecordToDelete(null);
    };

    const cancelEdit = () => {
        setEditingKey(null);
        setEditingValues({});
    };

    const saveRow = async record => {
        setUpdating(true);
        const updated = editingValues[record.oid];
        try {
            for (const field of meses) {
                const newVal = updated[field];
                if (newVal !== record[field]) {
                    const { status } = await updateAvanceIndicadorProyecto({ oid: record.oid, field, value: newVal });
                    if (status !== 200) throw new Error('Error actualizando ' + field);
                }
            }
            message.success('Avances guardados correctamente.');
            setAvances(prev => prev.map(item => item.oid === record.oid ? updated : item));
            setFilteredAvances(prev => prev.map(item => item.oid === record.oid ? updated : item));
            cancelEdit();
        } catch (e) {
            message.error(e.message || 'Error guardando avances.');
        } finally {
            setUpdating(false);
        }
    };

    // Columnas base
    const baseCols = [
        { title: 'Indicador', dataIndex: 'indicador_nombre', key: 'indicador_nombre', width: 250, fixed: 'left' },
        { title: 'Unidad', dataIndex: 'unidad_medida', key: 'unidad_medida', width: 150, align: 'center' },
        { title: 'Tipo', dataIndex: 'tipo_indicador', key: 'tipo_indicador', width: 180, align: 'center', render: txt => <Tag color={tipoIndicadorColors[txt]}>{txt}</Tag> }
    ];
    // Columnas de meses
    const mesCols = meses.map((mes, i) => ({
        title: etiquetasMeses[i],
        dataIndex: mes,
        key: mes,
        width: 120,
        align: 'center',
        render: (text, record) =>
            editingKey === record.oid ? (
                <Input
                    type='number'
                    value={editingValues[record.oid][mes]}
                    onChange={e =>
                        setEditingValues(prev => ({
                            ...prev,
                            [record.oid]: { ...prev[record.oid], [mes]: Number(e.target.value) }
                        }))
                    }
                    style={{ width: 80, textAlign: 'center' }}
                />
            ) : (
                text
            )
    }));
    // Columna de Acciones
    const actionCol = {
        title: 'Acción',
        key: 'accion',
        width: 100,
        fixed: 'right',
        render: (_, record) =>
            editingKey === record.oid ? (
                <Space>
                    <Button icon={<CloseOutlined />} onClick={cancelEdit} />

                    {permissionUpdate && (
                        <Button type='primary' icon={<SaveOutlined />} onClick={() => saveRow(record)} />
                    )}

                </Space>
            ) : (
                <Space>
                    {permissionUpdate && (
                        <Button icon={<EditOutlined />} onClick={() => startEdit(record)} />
                    )}
                    {permissionDelete && (
                        <Popconfirm
                            title="¿Desea eliminar este indicador?"
                            description="Esta acción no se puede deshacer."
                            onConfirm={handleConfirm}
                            onCancel={() => {
                                message.info('Eliminación cancelada');
                                setRecordToDelete(null);
                            }}
                            okText="Si"
                            cancelText="No"
                        >
                            <Button
                                icon={<DeleteOutlined />}
                                type="primary"
                                danger
                                onClick={() => setRecordToDelete(record)}  // <-- aquí guardas el record completo
                            />
                        </Popconfirm>
                    )}

                </Space>
            )
    };

    const columns = [...baseCols, ...mesCols, actionCol];

    return (
        <div>
            {(loading || updating) ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                    <Spin size="large" />
                </div>
            ) : (
                <div style={{ padding: 12, background: '#fff', borderRadius: 8 }}>
                    <Row justify='space-between' align='middle' style={{ marginBottom: 12 }}>
                        <Col flex='30%'>
                            <Title level={4}>Avances de Indicadores</Title>
                        </Col>
                        <Col flex='50%'>
                            <Space>
                                <Input
                                    placeholder='Buscar...'
                                    value={searchText}
                                    onChange={e => setSearchText(e.target.value)}
                                    allowClear
                                    style={{ width: 200 }}
                                />
                                <Select
                                    placeholder='Filtrar tipo'
                                    value={tipoFiltro}
                                    onChange={setTipoFiltro}
                                    allowClear
                                    style={{ width: 200 }}
                                >
                                    {Object.keys(tipoIndicadorColors).map(t => (
                                        <Option key={t} value={t}>{t}</Option>
                                    ))}
                                </Select>
                                <Button onClick={() => { setSearchText(''); setTipoFiltro(''); }}>Limpiar</Button>
                            </Space>
                        </Col>
                        <Col flex='20%' style={{ textAlign: 'right' }}>
                            <Switch
                                checkedChildren='Tabla'
                                unCheckedChildren='Gráfica'
                                checked={isTableView}
                                onChange={setIsTableView}
                            />
                        </Col>
                    </Row>
                    {isTableView ? (
                        <Table
                            dataSource={filteredAvances}
                            columns={columns}
                            rowKey='oid'
                            pagination={{ pageSize: 10 }}
                            scroll={{ x: 'max-content' }}
                            size='small'
                            bordered
                            sticky
                        />
                    ) : (
                        <IndicadoresLineChart data={filteredAvances} />
                    )}
                </div>
            )}
        </div>
    );
};

export default AvancesIndicadores;
